'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { cva } from "class-variance-authority";
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
const modalClasses = cva([
    'relative mx-auto px-5 md:px-10 py-10 rounded-xl bg-white drop-shadow-xl min-w-[335px] md:min-w-[375px]'
], {
    variants: {
        width: {
            sm: [
                'max-w-[420px]'
            ],
            md: [
                'max-w-screen-sm'
            ]
        }
    }
});
/**
 * Modal - [API](https://scl.sharp.com/?path=/docs/base-modal)
 */ const Modal = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { width = 'md', className, open, onClose, onCloseTransitionEnd, children, onTransitionEnd, onClick, onKeyDown, style } = _param, rest = _object_without_properties(_param, [
        "width",
        "className",
        "open",
        "onClose",
        "onCloseTransitionEnd",
        "children",
        "onTransitionEnd",
        "onClick",
        "onKeyDown",
        "style"
    ]);
    const dialogRef = useRef(null);
    const scrollbarWidth = typeof window !== 'undefined' ? window.innerWidth - document.documentElement.clientWidth : 0;
    const prevPaddingRef = useRef(null);
    useImperativeHandle(ref, ()=>dialogRef.current, []);
    const ourTransitionEnd = (event)=>{
        if (event.propertyName === 'opacity' && event.pseudoElement === '::backdrop') {
            onCloseTransitionEnd === null || onCloseTransitionEnd === void 0 ? void 0 : onCloseTransitionEnd(event);
        }
        onTransitionEnd === null || onTransitionEnd === void 0 ? void 0 : onTransitionEnd(event);
    };
    const ourClick = (event)=>{
        if (!(event.target instanceof HTMLElement)) return;
        if (event.target.getAttribute('data-backdrop')) {
            onClose === null || onClose === void 0 ? void 0 : onClose(event);
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    };
    const ourKeyDown = (event)=>{
        if (event.key === 'Escape') {
            onClose === null || onClose === void 0 ? void 0 : onClose(event);
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    };
    useEffect(()=>{
        const currentBodyPadding = +window.getComputedStyle(document.body).paddingRight.replace('px', '');
        prevPaddingRef.current = currentBodyPadding;
    }, []);
    useEffect(()=>{
        if (open) {
            var _dialogRef_current;
            if (scrollbarWidth) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }
            if (prevPaddingRef.current) {
                document.body.style.paddingRight = `${scrollbarWidth + prevPaddingRef.current}px`;
            }
            (_dialogRef_current = dialogRef.current) === null || _dialogRef_current === void 0 ? void 0 : _dialogRef_current.showModal();
        } else {
            var _dialogRef_current1;
            (_dialogRef_current1 = dialogRef.current) === null || _dialogRef_current1 === void 0 ? void 0 : _dialogRef_current1.close();
            if (prevPaddingRef.current === 0) {
                document.body.style.paddingRight = '';
            } else {
                document.body.style.paddingRight = `${prevPaddingRef.current}px`;
            }
        }
    }, [
        open
    ]);
    return(// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    /*#__PURE__*/ _jsx("dialog", _object_spread_props(_object_spread({}, rest), {
        ref: dialogRef,
        className: `first-line:focus-visible:outline-none
        fixed m-0 z-50 overflow-auto bg-transparent w-full max-w-full h-full max-h-full
        backdrop:bg-black/50 backdrop:backdrop-filter backdrop:backdrop-blur-sm

        opacity-0 scale-95 transition-all duration-200 delay-0
        backdrop:opacity-0 backdrop:transition-opacity backdrop:duration-200 backdrop:delay-100

        open:starting:opacity-0 open:starting:scale-95
        backdrop:open:starting:opacity-0

        open:block open:opacity-100 open:scale-100 open:ease-out open:duration-300 open:delay-100
        backdrop:open:opacity-100 backdrop:open:ease-out backdrop:open:duration-300 backdrop:open:delay-0

        text-inherit
       `,
        onTransitionEnd: ourTransitionEnd,
        onClick: ourClick,
        onKeyDown: ourKeyDown,
        style: _object_spread({
            transitionBehavior: 'allow-discrete'
        }, style !== null && style !== void 0 ? style : {}),
        children: /*#__PURE__*/ _jsx("div", {
            className: "fixed inset-0 flex items-start justify-center p-5 mx-auto overflow-auto",
            "data-backdrop": true,
            children: /*#__PURE__*/ _jsx("div", {
                className: "flex min-h-full items-center justify-center",
                "data-backdrop": true,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: modalClasses({
                        width
                    }),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: className,
                            children: children
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            color: "transparent",
                            size: "sm",
                            shape: "circle",
                            className: "absolute right-3 top-3",
                            onClick: onClose,
                            children: /*#__PURE__*/ _jsx(Icon, {
                                icon: "xmark-large",
                                title: "Close",
                                size: "lg"
                            })
                        })
                    ]
                })
            })
        })
    })));
});
export { Modal };
